import { useState, type ReactElement } from "react";
import { Modal, Tabs } from "@cntrl/ui";
import styles from "./SettingsModal.module.scss";

/** A tab this seat may see. The `cap` that decided that lives in the caller's own tab registry. */
export interface SettingsShellTab<K extends string> {
  key: K;
  label: string;
}

export interface SettingsModalShellProps<K extends string> {
  /** The tabs this seat may see, in order. Never empty — a caller with nothing to show renders nothing. */
  tabs: readonly SettingsShellTab<K>[];
  /** Which tab to open on. Ignored when this seat cannot see it (see below). */
  initialTab?: K;
  /** Tab key → its body. */
  bodies: Record<K, () => ReactElement>;
  /** Names the tablist for assistive tech, e.g. "Settings sections". */
  tabsLabel: string;
  onClose: () => void;
}

/**
 * The Settings dialog CHROME — a 2xl card of fixed height, the tab strip, and the one scrolling body — shared
 * by the band Settings modal and the org one.
 *
 * Two things it owns that are easy to get subtly wrong, which is why it is a component rather than a copied
 * twenty lines:
 *
 * - **Opening on the first tab the seat can actually SEE.** A hard-coded default, or an `initialTab` the
 *   caller may not hold the capability for, renders a body with no tab selected. Both modals have
 *   capability-gated tabs (`band:manage` on one, `roster:manage_seats` on the other), so both need it.
 * - **Tab state is LOCAL, not routed.** Opening the dialog must not push history the back button then has to
 *   unwind, and closing it must leave you exactly where you were.
 */
export function SettingsModalShell<K extends string>({
  tabs,
  initialTab,
  bodies,
  tabsLabel,
  onClose,
}: SettingsModalShellProps<K>) {
  const [active, setActive] = useState<K | undefined>(initialTab);

  const firstVisible = tabs[0]?.key;
  const activeTab = active && tabs.some((t) => t.key === active) ? active : firstVisible;
  if (!activeTab) return null;
  // Indexed out of a Record<K, …>, so TS knows only that it is one of the bodies — widen to the component
  // type the map is declared with rather than leaving the JSX element type generic.
  const Body: () => ReactElement = bodies[activeTab];

  return (
    // `bodyLayout="flush"`: the strip must PIN (the primitive's padded body would scroll it away with the
    // panel) and its hairline must span the card. The inset the panel needs lives on `.body` below.
    <Modal title="Settings" onClose={onClose} size="2xl" className={styles.card} bodyLayout="flush">
      <Tabs tabs={tabs} active={activeTab} onChange={setActive} label={tabsLabel} className={styles.tabs} />
      <div className={styles.body}>
        <Body />
      </div>
    </Modal>
  );
}
